import { useNavigate } from 'react-router';

import { shouldUseAcceptRequestModal } from '../common/shouldUseAcceptRequestModal';
import { execTripMenuAction } from '../execTripMenuAction';
import { useRemoveTripFromMultiLoad } from './useRemoveTripFromMultiLoad';
import { useAppDispatch, useAppSelector } from 'src/app/store/hooks';
import { useLazyAssignmentsRetrieveQuery } from 'src/common/external/bambi-api/assignmentsApi';
import type {
  DispatchTripRead,
  OverrideStatusEnum,
  PassengerRead,
} from 'src/common/external/bambi-api/bambiApi';
import type {
  NewStatusEnum,
  PatchedTrip,
} from 'src/common/external/bambi-api/tripsApi';
import {
  useLazyTripsRetrieveQuery,
  useTripsAcceptCancellationRequestPartialUpdateMutation,
  useTripsDestroyMutation,
  useTripsMultiloadDestroyMutation,
  useTripsRejectCancellationRequestPartialUpdateMutation,
} from 'src/common/external/bambi-api/tripsApi';
import { show } from 'src/common/primitives/Toast/toast.slice';
import { addATrip } from 'src/features/add-trip/addATrip.slice';
import { useTripRepository } from 'src/features/add-trip/useAddATripOnSubmit/useTripRepository';
import { useCopyText } from 'src/features/billing/invoices/modals/hooks/useCopyText';
import {
  dispatcherSlice,
  onRequestedTripAssignment,
  onTripAssignmentInvalid,
  onTripActionEnd,
  onTripMenuCancelRequest,
  onWillCallScheduleOverride,
  setModalConfirmTripActivationTripId,
  showDispatchModal,
} from 'src/features/dispatch/dispatcher.slice';
import {
  isScheduleOnlyExplanation,
  reorderScheduleExplanationLast,
  suggestedPickupTimeWithinCrewSchedule,
} from 'src/features/dispatch/DispatchRoute/willCallScheduleOverride';
import { selectEffectiveDispatchResponse } from 'src/features/dispatch/selectEffectiveDispatchResponse';
import { generateSubscriptionFromTrip } from 'src/features/trip/trip-subscriptions/trip-subscription-form/generateSubscriptionFromTrip';
import { tripSubscriptionsSlice } from 'src/features/trip/trip-subscriptions/trip-subscriptions.slice';
import { tripSlice } from 'src/features/trip/trip.slice';

export function useTripMenuActions() {
  const dispatch = useAppDispatch();
  const tripRepository = useTripRepository();
  const [getTrip] = useLazyTripsRetrieveQuery();
  const [deleteTrip] = useTripsDestroyMutation();
  const navigate = useNavigate();
  const copyText = useCopyText();

  const { updateDriverNotes } = useUpdateDriverNotesActions();

  const [deleteTripMultiLoad] = useTripsMultiloadDestroyMutation();

  const [getAssignment] = useLazyAssignmentsRetrieveQuery();
  const removeTripFromMultiLoad = useRemoveTripFromMultiLoad();

  const [acceptTripCancellationRequest] =
    useTripsAcceptCancellationRequestPartialUpdateMutation();
  const [rejectTripCancellationRequest] =
    useTripsRejectCancellationRequestPartialUpdateMutation();

  const nextToastId = useAppSelector((state: any) => state.toast.nextId);

  const dispatchResponse = useAppSelector(selectEffectiveDispatchResponse);

  const handleInvalidAssignment = (
    tripId: string,
    assignmentId: string,
    status: OverrideStatusEnum,
    // Accept the partial OpenAPI shape so callers don't have to pre-narrow.
    // The runtime backend always sends both fields, but defaulting here
    // keeps us safe if the contract ever changes.
    validateAssignmentResponse: {
      can_assign?: boolean;
      explanation?: string[];
    }
  ) => {
    const explanation = validateAssignmentResponse.explanation ?? [];
    const can_assign = validateAssignmentResponse.can_assign ?? false;
    const trip = dispatchResponse
      ? [
          ...dispatchResponse.unassigned_trips,
          ...dispatchResponse.assigned_trips,
        ].find((t) => t.id === tripId)
      : undefined;
    const assignment = dispatchResponse?.assignments.find(
      (a) => a.id === assignmentId
    );

    // Only skip the standard invalid-assignment modal when the schedule
    // problem is the entire story. If there are orthogonal violations,
    // the dispatcher should see them upfront in the invalid modal and
    // confirm the override; the reschedule modal can be reached from
    // that modal's Assign button (see useAssignTripFlow).
    if (
      trip?.is_non_activated_will_call &&
      assignment &&
      isScheduleOnlyExplanation(explanation)
    ) {
      const suggestedPickupAt = suggestedPickupTimeWithinCrewSchedule({
        pickupAt: trip.scheduled_pickup_at,
        dropoffAt: trip.scheduled_dropoff_at,
        crewStart: assignment.start,
        crewEnd: assignment.end,
      });
      if (suggestedPickupAt !== null) {
        dispatch(
          onWillCallScheduleOverride({
            tripId,
            assignmentId,
            status,
            originalPickupAt: trip.scheduled_pickup_at,
            suggestedPickupAt,
            crewStart: assignment.start,
            crewEnd: assignment.end,
          })
        );
        return;
      }
    }

    dispatch(
      onTripAssignmentInvalid({
        tripId,
        assignmentId,
        validateAssignmentResponse: {
          can_assign,
          explanation: reorderScheduleExplanationLast(explanation),
        },
        status,
      })
    );
  };

  return {
    updateDriverNotes,
    delete: (tripId: string) => {
      dispatch(
        execTripMenuAction({
          action: async () => {
            await deleteTrip({
              id: tripId,
            }).unwrap();
          },
          successMessage: 'Trip deleted successfully!',
          errorMessage: 'Error deleting trip',
        })
      );
    },
    viewEdit: async (tripId: string) => {
      //TODO handle loading and errors
      const res = await getTrip({
        id: tripId,
      });
      dispatch(addATrip.actions.setSelectedTrip(res.data));
      dispatch(addATrip.actions.selectPayer(res.data?.payer));
      dispatch(addATrip.actions.open());
    },
    addEditTags: (tripId: string) => {
      dispatch(tripSlice.actions.onAddTripTags(tripId));
    },
    editStatusUpdates: (tripId: string) => {
      dispatch(tripSlice.actions.onEditTripStatusUpdates(tripId));
    },
    clone: async (tripId: string) => {
      //TODO handle loading and errors
      const res = await getTrip({
        id: tripId,
      });
      if (!res.data) return;
      dispatch(addATrip.actions.cloneTrip(res.data));
    },
    ontime: (tripId: string) => {
      dispatch(
        execTripMenuAction({
          action: async () => {
            // TODO: Will need to be uncommented
            // Not seeing a map with new TripEventEnum
            //await tripRepository.ontime(tripId);
          },
          successMessage: 'Status set to On-Time',
          errorMessage: 'Error setting status to on-time',
        })
      );
    },
    delay: (tripId: string) => {
      dispatch(
        execTripMenuAction({
          action: async () => {
            // TODO: Will need to be uncommented
            // await tripRepository.delay(tripId);
          },
          successMessage: 'Status set to delayed',
          errorMessage: 'Error setting status to delayed',
        })
      );
    },
    cancel: async (tripId: string) => {
      const res = await getTrip({
        id: tripId,
      });
      dispatch(onTripMenuCancelRequest({ trip: res.data || null }));
    },
    uncancel: (tripId: string) => {
      dispatch(
        execTripMenuAction({
          action: async () => {
            await tripRepository.uncancel(tripId);
          },
          successMessage: 'Trip Uncanceled',
          errorMessage: 'Error canceling trip',
        })
      );
    },
    lock: (tripId: string) => {
      dispatch(
        execTripMenuAction({
          action: async () => {
            await tripRepository.lock(tripId);
          },
          successMessage: 'Trip locked',
          errorMessage: 'Error locking trip',
        })
      );
    },
    unlock: (tripId: string) => {
      dispatch(
        execTripMenuAction({
          action: async () => {
            await tripRepository.unlock(tripId);
          },
          successMessage: 'Trip unlocked',
          errorMessage: 'Error unlocking trip',
        })
      );
    },
    assign: async (
      tripId: string,
      assignmentId: string,
      status: OverrideStatusEnum
    ) => {
      let validateAssignmentResponse: Awaited<
        ReturnType<typeof tripRepository.validateAssignment>
      >;
      try {
        validateAssignmentResponse = await tripRepository.validateAssignment(
          tripId,
          assignmentId
        );
      } catch {
        dispatch(
          show({
            description: 'Error validating trip assignment',
            type: 'error',
          })
        );
        return;
      }
      if (validateAssignmentResponse.can_assign) {
        const trip = dispatchResponse
          ? [
              ...dispatchResponse.unassigned_trips,
              ...dispatchResponse.assigned_trips,
            ].find((t) => t.id === tripId)
          : undefined;
        if (
          shouldUseAcceptRequestModal({
            intermediary: trip?.intermediary,
            status,
          })
        ) {
          dispatch(
            onRequestedTripAssignment({
              tripId,
              assignmentId,
            })
          );
        } else {
          dispatch(
            execTripMenuAction({
              action: async () => {
                await tripRepository.assign(tripId, assignmentId);
              },
              successMessage: 'Trip assigned',
              errorMessage: 'Error assigning trip',
            })
          );
        }
      } else {
        handleInvalidAssignment(
          tripId,
          assignmentId,
          status,
          validateAssignmentResponse
        );
      }
    },
    multiloadAssign: (
      tripId: string,
      assignmentId: string,
      potentialMultiloadTrips: string[]
    ) => {
      dispatch(
        dispatcherSlice.actions.onMultiloadAssignmentStart({
          tripId,
          assignmentId,
          potentialMultiloadTrips,
        })
      );
    },
    addToExistingMultiLoad: (
      tripId: string,
      assignmentId: string,
      existingMultiloadTripIds: string[],
      potentialMultiloadTrips?: string[]
    ) => {
      dispatch(
        dispatcherSlice.actions.onAddToExistingMultiLoad({
          tripId,
          assignmentId,
          existingMultiloadTripIds,
          potentialMultiloadTrips,
        })
      );
    },
    assignToExistingMultiLoad: (
      tripId: string,
      assignmentId: string,
      existingMultiloadTripIds: string[],
      potentialMultiloadTrips?: string[]
    ) => {
      dispatch(
        dispatcherSlice.actions.onAssignToExistingMultiLoad({
          tripId,
          assignmentId,
          existingMultiloadTripIds,
          potentialMultiloadTrips,
        })
      );
    },
    removeTripFromMultiLoad: (tripId: string, multiLoadId: string) => {
      dispatch(
        execTripMenuAction({
          action: async () => {
            await removeTripFromMultiLoad(tripId, multiLoadId);
          },
          successMessage: 'Trip assigned',
          errorMessage: 'Error removing multi-loaded trip',
        })
      );
    },
    removeMultiLoad: (multiLoadId: string) => {
      dispatch(
        execTripMenuAction({
          action: async () => {
            await deleteTripMultiLoad({ id: multiLoadId });
          },
          successMessage: 'Multi-load removed',
          errorMessage: 'Error removing multi-load',
        })
      );
    },
    editMultiLoad: async (trip: DispatchTripRead, assignmentId: string) => {
      const assignmentResponse = await getAssignment({ id: assignmentId });
      if (!assignmentResponse.data) {
        throw new Error(`Error getting assignment with id: ${assignmentId}`);
      }
      dispatch(
        dispatcherSlice.actions.onMultiloadAssignmentEdit({
          trip,
          assignment: assignmentResponse.data,
        })
      );
    },
    createAssignedTripMultiLoad: (
      trip: DispatchTripRead,
      potentialMultiloadTrips: string[]
    ) => {
      dispatch(
        dispatcherSlice.actions.onCreateAssignedTripMultiLoad({
          trip,
          potentialMultiloadTrips,
        })
      );
    },
    reassign: async (
      tripId: string,
      assignmentId: string,
      status: OverrideStatusEnum,
      multiLoadId: string | null
    ) => {
      let validateAssignmentResponse: Awaited<
        ReturnType<typeof tripRepository.validateAssignment>
      >;
      try {
        validateAssignmentResponse = await tripRepository.validateAssignment(
          tripId,
          assignmentId
        );
      } catch {
        dispatch(
          show({
            description: 'Error validating trip assignment',
            type: 'error',
          })
        );
        // Terminal for a drag-initiated reassign: drop its destination
        // preview along with the flow. No-op when there is no pending action.
        dispatch(onTripActionEnd());
        return;
      }
      if (validateAssignmentResponse.can_assign) {
        dispatch(
          execTripMenuAction({
            action: async () => {
              await tripRepository.reassign(tripId, assignmentId);
              if (multiLoadId) {
                await removeTripFromMultiLoad(tripId, multiLoadId);
              }
            },
            successMessage: 'Trip reassigned',
            errorMessage: 'Error reassigning trip',
          })
        );
      } else {
        handleInvalidAssignment(
          tripId,
          assignmentId,
          status,
          validateAssignmentResponse
        );
      }
    },
    unassign: (tripId: string, multiLoadId: string | null) => {
      dispatch(
        execTripMenuAction({
          action: async () => {
            await tripRepository.unassign(tripId);
            if (multiLoadId) {
              await removeTripFromMultiLoad(tripId, multiLoadId);
            }
          },
          successMessage: 'Trip unassigned',
          errorMessage: 'Error unassigning trip',
          serverErrorMessageFormatter: (error) => {
            const message = error?.data?.errors?.find(
              (e: any) => e.attr === 'message'
            );

            if (!message?.detail) {
              return null;
            }

            return `Unassign Error: ${message.detail}`;
          },
        })
      );
    },
    unassignMultiload: (trips: DispatchTripRead[], multiLoadId: string) => {
      dispatch(
        execTripMenuAction({
          action: async () => {
            // Wait-and-return inbound legs are unassigned server-side along
            // with their outbound leg; unassigning them directly would double
            // up (mirrors deriveMultiloadReassignInput's cascade handling).
            const cascadedInboundIds = new Set(
              trips.flatMap((t) => (t.inbound_trip ? [t.inbound_trip.id] : []))
            );
            await Promise.all(
              trips
                .filter((trip) => !cascadedInboundIds.has(trip.id))
                .map((trip) => tripRepository.unassign(trip.id))
            );
            // The whole group has left the assignment — remove the multiload
            // record outright instead of patching it down member by member.
            await deleteTripMultiLoad({ id: multiLoadId });
          },
          successMessage: 'Trips unassigned',
          errorMessage: 'Error unassigning trips',
          serverErrorMessageFormatter: (error) => {
            const message = error?.data?.errors?.find(
              (e: any) => e.attr === 'message'
            );

            if (!message?.detail) {
              return null;
            }

            return `Unassign Error: ${message.detail}`;
          },
        })
      );
    },
    complete: (tripId: string) => {
      dispatch(
        execTripMenuAction({
          action: async () => {
            await tripRepository.complete(tripId);
          },
          successMessage: 'Trip completed',
          errorMessage: 'Error completing trip',
        })
      );
    },
    completeWithNoValidations: (tripId: string) => {
      dispatch(
        execTripMenuAction({
          action: async () => {
            await tripRepository.completeWithNoValidations(tripId);
          },
          successMessage: 'Trip completed',
          errorMessage: 'Error completing trip',
        })
      );
    },
    updatePickupTime: ({
      tripId,
      newTime,
    }: {
      tripId: string;
      newTime: string;
    }) => {
      dispatch(
        execTripMenuAction({
          action: async () => {
            // TODO: Actualy update the time
            await tripRepository.updatePickupTime(tripId, newTime);
          },
          successMessage: 'Pickup time updated',
          errorMessage: 'Error updating time',
        })
      );
    },
    updatePickupWaitTime: ({
      tripId,
      waitTimeInSeconds,
    }: {
      tripId: string;
      waitTimeInSeconds: number | null;
    }) => {
      dispatch(
        execTripMenuAction({
          action: async () => {
            // TODO: Actualy update the time
            await tripRepository.updateTrip(tripId, {
              pickup_wait_time_seconds: waitTimeInSeconds,
            } as PatchedTrip);
          },
          successMessage: 'Pickup wait time updated',
          errorMessage: 'Error updating wait time',
        })
      );
    },
    activateWillCall: ({ tripId }: { tripId: string }) => {
      dispatch(setModalConfirmTripActivationTripId(tripId));
      dispatch(showDispatchModal('confirmTripActivation'));
    },
    revertStatus: (tripId: string) => {
      dispatch(
        execTripMenuAction({
          action: async () => {
            await tripRepository.revertStatus(tripId);
          },
          successMessage: 'Trip status reverted',
          errorMessage: 'Error reverting trip status',
        })
      );
    },
    overrideStatus: (tripId: string, newStatus: NewStatusEnum) => {
      dispatch(
        execTripMenuAction({
          action: async () => {
            await tripRepository.overrideStatus(tripId, newStatus);
          },
          successMessage: `Trip status set to ${newStatus}`,
          errorMessage: `Error setting status to ${newStatus}`,
        })
      );
    },
    updateDispatcherNotes: (tripId: string, note: string) => {
      dispatch(
        execTripMenuAction({
          action: async () => {
            await tripRepository.updateTrip(tripId, {
              dispatcher_notes: note,
            } as PatchedTrip);
          },
          successMessage: `Trip Notes Updated`,
          errorMessage: `Error updating trip notes`,
        })
      );
    },
    goToTripSummary: (tripId: string) => {
      navigate(`/trips/${tripId}/summary`);
    },
    rejectMarketplaceRequest: (tripId: string) => {
      dispatch(
        dispatcherSlice.actions.onSingleTripRequestRejection({ tripId })
      );
    },
    acceptMarketplaceRequest: (tripId: string) => {
      dispatch(
        dispatcherSlice.actions.onSingleTripRequestAcceptance({ tripId })
      );
    },
    copyTripId: (tripId: string) => {
      copyText(tripId);
    },
    acceptCancellationRequest: (tripId: string) => {
      dispatch(
        execTripMenuAction({
          action: async () => {
            await acceptTripCancellationRequest({
              id: tripId,
            });
          },
          successMessage: 'Accepted trip cancellation',
          errorMessage: 'Error accepting trip cancellation',
        })
      );
    },
    rejectCancellationRequest: (tripId: string) => {
      dispatch(
        execTripMenuAction({
          action: async () => {
            await rejectTripCancellationRequest({
              id: tripId,
            });
          },
          successMessage: 'Rejected trip cancellation',
          errorMessage: 'Error rejecting trip cancellation',
        })
      );
    },
    createSubscriptionFromTrip: async (tripId: string) => {
      const res = await getTrip({
        id: tripId,
      });

      if (!res.data) {
        dispatch(
          show({
            id: nextToastId,
            title: 'Trip not found',
            description: 'The trip was not found',
            type: 'error',
          })
        );
        return;
      }

      const subscription = generateSubscriptionFromTrip(res.data);

      dispatch(
        tripSubscriptionsSlice.actions.setSelectedTripSubscription({
          tripSubscription: subscription,
          passenger: res.data.passenger as PassengerRead,
        })
      );

      dispatch(
        tripSubscriptionsSlice.actions.setShowTripSubscriptionModal(true)
      );
    },
  };
}

type LocationType = 'pickup' | 'dropoff';

function useUpdateDriverNotesActions() {
  const dispatch = useAppDispatch();
  const tripRepository = useTripRepository();

  const updateDriverNotes = (
    tripId: string,
    note: string,
    location: LocationType
  ) => {
    dispatch(
      execTripMenuAction({
        action: async () => {
          const trip = await tripRepository.getTrip(tripId);
          await tripRepository.updateTrip(tripId, {
            [location]: {
              ...trip[location],
              driver_notes: note,
            },
          } as PatchedTrip);
        },
        successMessage: `Driver ${location} notes updated`,
        errorMessage: `Error updating driver ${location} notes`,
      })
    );
  };

  return {
    updateDriverNotes,
  };
}
